import {
  Flex,
  Table,
  Tbody,
  Td,
  Tfoot,
  Th,
  Thead,
  Tr,
  useColorMode,
} from '@chakra-ui/react';
import {
  Column,
  flexRender,
  Row,
  Table as TanStackTable,
} from '@tanstack/react-table';
import React from 'react';

import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
} from 'packages/common-frontend/theme/tokens';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { SortIcon } from '~/packages/common-frontend/tables/SortIcon';
import { createResolveColor } from '~/packages/common-frontend/theme/utils';

// Hooks
function getFixedStyles<TData>(column: Column<TData, unknown>) {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn =
    isPinned === 'left' && column.getIsLastColumn('left');

  const isFirstRightPinnedColumn =
    isPinned === 'right' && column.getIsFirstColumn('right');

  const styles = {
    position: isPinned ? 'sticky' : 'relative',
    width: `${column.getSize()}px`,
    minWidth: `${column.getSize()}px`,
    zIndex: isPinned ? 1 : undefined,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
  } as const;

  if (isLastLeftPinnedColumn) {
    return {
      ...styles,
      boxShadow: '0 0 4px hsl(0deg 0% 76% / 35%)',
      clipPath: 'inset(0px -4px 0px 0px)',
    };
  }
  if (isFirstRightPinnedColumn) {
    return {
      ...styles,
      boxShadow: '0 0 4px hsl(0deg 0% 76% / 35%)',
      clipPath: 'inset(0px 0px 0px -4px)',
    };
  }
  return styles;
}

// Exports
type TableHeaderBETAProps<TData extends Record<string, unknown>> = {
  table: TanStackTable<TData>;
};
export function TableHeaderBETA<TData extends Record<string, unknown>>({
  table,
}: TableHeaderBETAProps<TData>) {
  const { colorMode } = useColorMode();
  const resolveColor = createResolveColor({ colorMode, colorScheme: 'gray' });

  // At least one column definition must have a specified `header` for the header row to show up
  const hasHeaderRow = table
    .getHeaderGroups()
    .map((group) =>
      group.headers.map((header) => header.column.columnDef.header),
    )
    .flat()
    .some((columnDef) => !!columnDef);
  if (!hasHeaderRow) return null;

  return (
    <Thead>
      {table.getHeaderGroups().map((headerGroup) => (
        <Tr
          key={headerGroup.id}
          position="sticky"
          top="0"
          // Fixed columns are zIndex=2, fixed rows need to be 1 higher
          zIndex="3"
        >
          {headerGroup.headers.map((header, cellIndex, cells) => (
            <Th
              key={header.id}
              onClick={header.column.getToggleSortingHandler()}
              background={resolveColor(BACKGROUND_COLORS.muted.base)}
              cursor={header.column.getCanSort() ? 'pointer' : 'default'}
              padding="0.5rem 0.75rem"
              colSpan={header.colSpan}
              boxSizing="border-box"
              verticalAlign="top"
              borderStyle="solid"
              borderWidth="0 1px 1px 0"
              borderColor={resolveColor(BORDER_COLORS.muted.hover)}
              borderRightWidth={cellIndex === cells.length - 1 ? '0' : '1px'}
              {...getFixedStyles(header.column)}
            >
              <Typography inline variant="base" fontWeight="medium">
                <Flex
                  as="span"
                  alignItems="baseline"
                  justifyContent="space-between"
                  gap="0.5rem"
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                  <SortIcon
                    columnCanSort={header.column.getCanSort()}
                    sorted={header.column.getIsSorted()}
                  />
                </Flex>
              </Typography>
            </Th>
          ))}
        </Tr>
      ))}
    </Thead>
  );
}
type TableBodyBETAProps<TData extends Record<string, unknown>> = {
  table: TanStackTable<TData>;
  clickableRowFilter?: (row: Row<TData>) => boolean;
  onRowClick?: (
    row: Row<TData>,
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
  ) => void;
};
export function TableBodyBETA<TData extends Record<string, unknown>>({
  table,
  clickableRowFilter = () => true,
  onRowClick,
}: TableBodyBETAProps<TData>) {
  const { colorMode } = useColorMode();
  const resolveColor = createResolveColor({ colorMode });

  return (
    <Tbody>
      {table.getRowModel().rows.map((row, rowIndex) => {
        const isRowClickable = !!onRowClick && clickableRowFilter(row);
        return (
          <Tr
            key={row.id}
            onClick={
              isRowClickable ? (event) => onRowClick(row, event) : undefined
            }
            sx={
              isRowClickable
                ? {
                    cursor: 'pointer',
                    '&:hover td': {
                      background: resolveColor(BACKGROUND_COLORS.white.hover),
                      borderColor: resolveColor(BORDER_COLORS.muted.hover),
                    },
                    '&:active td': {
                      background: resolveColor(BACKGROUND_COLORS.white.active),
                      borderColor: resolveColor(BORDER_COLORS.muted.active),
                    },
                  }
                : undefined
            }
          >
            {row.getVisibleCells().map((cell, cellIndex, cells) => (
              <Td
                key={cell.id}
                backgroundColor={
                  row.getIsSelected()
                    ? resolveColor(BACKGROUND_COLORS.white.hover)
                    : resolveColor(BACKGROUND_COLORS.white.base)
                }
                padding="0.75rem"
                boxSizing="border-box"
                borderColor={resolveColor(BORDER_COLORS.muted.base)}
                borderStyle="solid"
                borderTopWidth={rowIndex === 0 ? '0' : '1px'}
                borderBottomWidth="0"
                borderRightWidth={cellIndex === cells.length - 1 ? '0' : '1px'}
                verticalAlign="top"
                {...getFixedStyles(cell.column)}
              >
                <Typography inline variant="base-muted">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Typography>
              </Td>
            ))}
          </Tr>
        );
      })}
    </Tbody>
  );
}

type TableFooterBETAProps<TData extends Record<string, unknown>> = {
  table: TanStackTable<TData>;
};
export function TableFooterBETA<TData extends Record<string, unknown>>({
  table,
}: TableFooterBETAProps<TData>) {
  const { colorMode } = useColorMode();
  const resolveGray = createResolveColor({ colorMode, colorScheme: 'gray' });
  const resolveAurora = createResolveColor({
    colorMode,
    colorScheme: 'v2-aurora',
  });

  // At least one column definition must have a specified `footer` for the footer row to show up
  const hasFooterRow = table
    .getFooterGroups()
    .map((group) =>
      group.headers.map((header) => header.column.columnDef.footer),
    )
    .flat()
    .some((columnDef) => !!columnDef);
  if (!hasFooterRow) return null;

  return (
    <Tfoot>
      {table.getFooterGroups().map((footerGroup) => (
        <Tr
          key={footerGroup.id}
          position="sticky"
          bottom="0"
          // Fixed columns are zIndex=2, fixed rows need to be 1 higher
          zIndex="3"
        >
          {footerGroup.headers.map((header, cellIndex, cells) => (
            <Th
              key={header.id}
              background={resolveAurora(BACKGROUND_COLORS.muted.base)}
              padding="0.5rem 0.75rem"
              colSpan={header.colSpan}
              whiteSpace="nowrap"
              borderStyle="solid"
              borderColor={resolveGray(BORDER_COLORS.muted.hover)}
              borderWidth="1px 1px 0 0"
              // If this is the last cell, don't show border. The whole table is wrapped in a border
              borderRightWidth={cellIndex === cells.length - 1 ? '0' : '1px'}
              boxSizing="border-box"
              {...getFixedStyles(header.column)}
            >
              <Typography inline variant="base" fontWeight="medium">
                <Flex
                  as="span"
                  alignItems="baseline"
                  justifyContent="space-between"
                  gap="0.5rem"
                >
                  {flexRender(
                    header.column.columnDef.footer,
                    header.getContext(),
                  )}
                </Flex>
              </Typography>
            </Th>
          ))}
        </Tr>
      ))}
    </Tfoot>
  );
}

type TableBETAProps<TData extends Record<string, unknown>> = {
  table: TanStackTable<TData>;
  clickableRowFilter?: TableBodyBETAProps<TData>['clickableRowFilter'];
  onRowClick?: TableBodyBETAProps<TData>['onRowClick'];
  FlexContainerProps?: React.ComponentProps<typeof Flex>;
};
export function TableBETA<TData extends Record<string, unknown>>({
  table,
  clickableRowFilter,
  onRowClick,
  FlexContainerProps,
}: TableBETAProps<TData>) {
  const { colorMode } = useColorMode();
  const resolveColor = createResolveColor({ colorMode, colorScheme: 'gray' });
  return (
    <Flex
      borderWidth="thin"
      borderColor={resolveColor(BORDER_COLORS.muted.base)}
      overflowX="auto"
      overflowY="auto"
      position="relative"
      width="100%"
      {...FlexContainerProps}
    >
      <Table style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
        <TableHeaderBETA table={table} />
        <TableBodyBETA
          table={table}
          clickableRowFilter={clickableRowFilter}
          onRowClick={onRowClick}
        />
        <TableFooterBETA table={table} />
      </Table>
    </Flex>
  );
}
